import { DropdownMenu } from 'radix-ui'
import type * as React from 'react'

import { cn } from '@/lib/utils'

import { Item } from '@/components/ui/item'
import { List } from '@/components/ui/list'

/*
 * Lista suspensa do header gov.br — o "Padrão Dropdown" que a anatomia do Header
 * cita ao lado do avatar do usuário e dos grupos de links e funcionalidades.
 *
 * É componente do projeto porque o registry não publica um: no DS o dropdown não
 * é um componente próprio, é um `.br-list` posicionado sob um gatilho. A
 * aparência vem inteira das peças que o registry publica (`List` e `Item`, que
 * são aquele `.br-list`); o que falta ali é comportamento — abrir e fechar,
 * navegação por setas, Escape, foco preso e devolvido ao gatilho —, e isso vem
 * do primitivo do Radix, sem reimplementação.
 */

function Dropdown(props: React.ComponentProps<typeof DropdownMenu.Root>) {
  return <DropdownMenu.Root {...props} />
}

function DropdownTrigger(props: React.ComponentProps<typeof DropdownMenu.Trigger>) {
  return <DropdownMenu.Trigger data-slot="dropdown-trigger" {...props} />
}

function DropdownContent({
  className,
  children,
  sideOffset = 4,
  align = 'end',
  ...props
}: React.ComponentProps<typeof DropdownMenu.Content>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        data-slot="dropdown-content"
        sideOffset={sideOffset}
        align={align}
        // O painel é o `.br-list` do DS: fundo próprio, sombra e a camada acima
        // do cabeçalho. A largura mínima acompanha a do gatilho, que o Radix
        // publica em `--radix-dropdown-menu-trigger-width`.
        className={cn(
          'z-(--z-index-layer-2) overflow-hidden',
          'min-w-(--radix-dropdown-menu-trigger-width)',
          '[box-shadow:var(--surface-shadow-md)]',
          className
        )}
        {...props}
      >
        <List>{children}</List>
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  )
}

function DropdownItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenu.Item>) {
  return (
    // `asChild` empresta ao item do Radix a anatomia da linha do DS: quem fica
    // no DOM é o `Item`, com o comportamento de menu por cima. O conteúdo vai
    // explicitamente no `Item` — o `Slot` do Radix descarta os children que
    // chegam por props, e o painel abriria vazio.
    <DropdownMenu.Item asChild {...props}>
      <Item interactive className={cn('flex items-center gap-(--spacing-scale-2x)', className)}>
        {children}
      </Item>
    </DropdownMenu.Item>
  )
}

export { Dropdown, DropdownContent, DropdownItem, DropdownTrigger }
